import moment from 'moment';
import { useEffect, useRef, useState } from 'react';
import { getAllReport } from 'services/API/Report';
import { ExportReport } from 'services/API/Report/interface';

type ReturnUseGetData = [
  isLoading: boolean,
  reports: Array<ExportReport>,
  loadReports: () => Promise<void>,
];

const useGetData = (type: string): ReturnUseGetData => {
  const [isLoading, setIsLoading] = useState(false);
  const [reports, setReports] = useState<Array<ExportReport>>([]);
  const currentState = useRef({ reports });
  currentState.current = { reports };
  const loadDataInterval = useRef<NodeJS.Timeout>();
  const abortController = useRef<AbortController | null>(null);
  const refreshInterval = 20000;

  const updateReportStatus = (report: ExportReport) => {
    report.isLoading = false;
    if (!report.file_name && !report.path) {
      report.isLoading = true;
    }

    let reportLoadFailed = false;
    const reportUpdateDiffMin = moment().diff(report.updated_at, 'm');
    const reportCreateDiffMin = moment().diff(report.created_at, 'm');
    if (report.status === 2) {
      reportLoadFailed = true;
    }

    if (!reportLoadFailed && report.isLoading) {
      if (reportUpdateDiffMin >= 30) {
        reportLoadFailed = true;
      }
    } else if (!reportLoadFailed && report.status === 0) {
      if (reportCreateDiffMin >= 60) {
        // set report to fail when status is not available and created at is more than 60 minutes
        reportLoadFailed = true;
      } else {
        report.isLoading = true;
      }
    }

    if (reportLoadFailed) {
      report.path = '';
      report.file_name = 'X';
      report.isLoading = false;
    }

    return report;
  };

  const getData = async (forceLoading = false) => {
    // skip if already loading
    if (abortController.current) {
      abortController.current.abort();
    }
    const controller = new AbortController();
    abortController.current = controller;

    const nextReports = currentState.current.reports.map(updateReportStatus);
    setReports(nextReports);
    setIsLoading(true);

    try {
      const response = await getAllReport(
        {
          type,
          limit: 5,
          'disable-debug': 1,
        },
        controller.signal,
      );

      if (controller.signal.aborted) {
        return;
      }

      setReports(response.data?.map(updateReportStatus));
    } catch (error) {
      if ((error as any)?.code === 'ERR_CANCELED' || controller.signal.aborted) {
        return;
      }
    } finally {
      if (!controller.signal.aborted) {
        setIsLoading(false);
      }
    }
  };

  const loadReports = () => getData(true);

  useEffect(() => {
    getData();

    startRefreshInterval(getData);

    return () => {
      if (abortController.current) {
        abortController.current.abort();
      }
      if (loadDataInterval.current) {
        clearInterval(loadDataInterval.current);
      }
    };
  }, []);

  useEffect(() => {
    const isEmptyResult = reports && reports.length === 0;
    if (reports && reports.length > 0) {
      const allReportsLoaded = reports.every((r) => !r.isLoading);
      clearInterval(loadDataInterval.current);
      if (!allReportsLoaded && !isEmptyResult) {
        startRefreshInterval(getData);
      }
    } else {
      if (loadDataInterval.current) {
        clearInterval(loadDataInterval.current);
      }
    }
  }, [reports]);

  // function to start the interval
  const startRefreshInterval = (fn: () => Promise<void>) => {
    loadDataInterval.current = setInterval(fn, refreshInterval);
  };

  return [isLoading, reports, loadReports];
};

export { useGetData };
